/**
 * Scenario progression authoring — client types and the editable draft.
 *
 * Mirrors the server's `domain/conversation/progression.ts` (schemaVersion 1)
 * and `condition.ts`. The server is the validator: the draft only shapes what
 * the author entered into the definition the server checks, and the server's
 * problems are shown back against the section they concern.
 */

export type ConditionKind =
  | 'concern_satisfied'
  | 'all_required_concerns_satisfied'
  | 'phase_reached'
  | 'min_user_turns'
  | 'max_duration_ms'
  | 'goal_met';

export interface Condition {
  kind: ConditionKind;
  ref?: string;
  threshold?: number;
  description?: string;
}

export interface RubricRef {
  id: string;
  version: string;
}

/** The modalities a progression may name a rubric for (server: RUBRIC_APPLICABLE_MODALITIES). */
export const RUBRIC_MODALITIES = ['text', 'realtime_audio', 'realtime_audio_video'] as const;
export type RubricModality = (typeof RUBRIC_MODALITIES)[number];

export const MODALITY_LABELS: Record<RubricModality, string> = {
  text: 'Written Chat',
  realtime_audio: 'Live Conversation (voice)',
  realtime_audio_video: 'Video',
};

/** Why a modality is not gradable (server: AssessmentUnavailableReason). */
export interface AssessmentUnavailableReason {
  status: 'CONTENT_INCOMPLETE' | 'ASSESSMENT_UNAVAILABLE';
  code: string;
  modality: string;
  mode: 'training' | 'testing';
  missingEvidence?: string[];
}

export interface ModalityGradability {
  modality: RubricModality;
  rubric: RubricRef | null;
  gradable: boolean;
  reason: AssessmentUnavailableReason | null;
}

export interface ProgressionDefinition {
  schemaVersion: 1;
  phases: string[];
  transitions?: Record<string, string[]>;
  facts?: { id: string; statement: string }[];
  requiredConcernRefs?: string[];
  allowedActions?: string[];
  rubric?: RubricRef;
  /** Explicit applicability: the rubric for each modality offered for Testing. */
  modalityRubrics?: Partial<Record<RubricModality, RubricRef>>;
  successConditions: Condition[];
  failureConditions?: Condition[];
}

export interface AuthoringConcern {
  ref: string;
  summary: string;
  intensity: number;
  phase: string | null;
}

export interface ProgressionAuthoringView {
  scenarioId: string;
  definition: ProgressionDefinition | null;
  contentStatus: 'complete' | 'content_incomplete';
  validation: { valid: boolean; problems: string[] };
  gradable: { testing: boolean; reason: string | null; modalities?: ModalityGradability[] };
  updatedAt: string | null;
  vocabulary: {
    concerns: AuthoringConcern[];
    requiredPhases: string[];
    defaultPhases: string[];
    actionKinds: string[];
    conditionKinds: ConditionKind[];
    modalities: string[];
  };
  tags: string[];
  examTags: { tag: string; exams: { key: string; label: string }[] }[];
}

export interface RubricSummary {
  id: string;
  version: string;
  title: string;
  scope: 'general_communication' | 'scenario' | 'interview_practice' | 'language';
  modalities: string[];
  /** Can be the single `rubric` (supports written and voice). */
  supportsScenarioModalities: boolean;
  /** Evidence classes its dimensions judge. */
  evidence?: string[];
  /** Modalities a GRADED attempt can be held to it in (modality + required evidence). */
  gradableModalities?: string[];
  dimensions: { key: string; label: string; description: string }[];
}

/** The editable form of a definition. `null` means "not authored: engine default". */
export interface ProgressionDraft {
  phases: string[];
  /** Per phase: null → any later phase (engine default); a list → only those. */
  transitions: Record<string, string[] | null>;
  facts: { id: string; statement: string }[];
  /** null → inferred from objection intensity. */
  requiredConcernRefs: string[] | null;
  /** null → every action is permitted. */
  allowedActions: string[] | null;
  rubric: RubricRef | null;
  /**
   * null → the single-rubric form (`rubric`). An object → explicit
   * per-modality applicability: only the modalities named are offered for
   * Testing, and `rubric` is not sent.
   */
  modalityRubrics: Partial<Record<RubricModality, RubricRef>> | null;
  successConditions: Condition[];
  failureConditions: Condition[];
}

export const CONDITION_LABELS: Record<ConditionKind, string> = {
  concern_satisfied: 'A concern is satisfied',
  all_required_concerns_satisfied: 'All required concerns are satisfied',
  phase_reached: 'A phase is reached',
  min_user_turns: 'At least N user turns',
  max_duration_ms: 'Duration exceeds N ms',
  goal_met: 'The engine reports the goal met',
};

export function conditionNeedsRef(kind: ConditionKind): 'phase' | 'concern' | null {
  if (kind === 'phase_reached') return 'phase';
  if (kind === 'concern_satisfied') return 'concern';
  return null;
}

export function conditionNeedsThreshold(kind: ConditionKind): boolean {
  return kind === 'min_user_turns' || kind === 'max_duration_ms';
}

/** A fresh draft: nothing invented beyond the engine's own default phases. */
export function emptyDraft(defaultPhases: readonly string[]): ProgressionDraft {
  return {
    phases: [...defaultPhases],
    transitions: {},
    facts: [],
    requiredConcernRefs: null,
    allowedActions: null,
    rubric: null,
    modalityRubrics: null,
    successConditions: [],
    failureConditions: [],
  };
}

export function draftFromDefinition(def: ProgressionDefinition): ProgressionDraft {
  return {
    phases: [...def.phases],
    transitions: Object.fromEntries(Object.entries(def.transitions ?? {}).map(([k, v]) => [k, [...v]])),
    facts: (def.facts ?? []).map((f) => ({ ...f })),
    requiredConcernRefs: def.requiredConcernRefs ? [...def.requiredConcernRefs] : null,
    allowedActions: def.allowedActions ? [...def.allowedActions] : null,
    rubric: def.rubric ? { ...def.rubric } : null,
    modalityRubrics: def.modalityRubrics
      ? Object.fromEntries(Object.entries(def.modalityRubrics).map(([m, r]) => [m, { ...r! }]))
      : null,
    successConditions: def.successConditions.map((c) => ({ ...c })),
    failureConditions: (def.failureConditions ?? []).map((c) => ({ ...c })),
  };
}

function cleanCondition(c: Condition): Condition {
  const out: Condition = { kind: c.kind };
  if (conditionNeedsRef(c.kind) && c.ref) out.ref = c.ref;
  if (conditionNeedsThreshold(c.kind) && typeof c.threshold === 'number' && Number.isFinite(c.threshold)) {
    out.threshold = c.threshold;
  }
  if (c.description && c.description.trim()) out.description = c.description.trim();
  return out;
}

/** The definition the server validates. Unauthored optional parts are omitted, not defaulted. */
export function definitionFromDraft(draft: ProgressionDraft): ProgressionDefinition {
  const transitions = Object.fromEntries(
    Object.entries(draft.transitions).filter((e): e is [string, string[]] => Array.isArray(e[1])),
  );
  const def: ProgressionDefinition = {
    schemaVersion: 1,
    phases: draft.phases.map((p) => p.trim()),
    successConditions: draft.successConditions.map(cleanCondition),
  };
  if (Object.keys(transitions).length) def.transitions = transitions;
  if (draft.facts.length) def.facts = draft.facts.map((f) => ({ id: f.id.trim(), statement: f.statement.trim() }));
  if (draft.requiredConcernRefs) def.requiredConcernRefs = [...draft.requiredConcernRefs];
  if (draft.allowedActions) def.allowedActions = [...draft.allowedActions];
  if (draft.modalityRubrics) {
    // Explicit applicability: only modalities with a chosen rubric are offered.
    const named = Object.entries(draft.modalityRubrics).filter((e): e is [RubricModality, RubricRef] => !!e[1]);
    if (named.length) def.modalityRubrics = Object.fromEntries(named.map(([m, r]) => [m, { ...r }]));
  } else if (draft.rubric) {
    def.rubric = { ...draft.rubric };
  }
  if (draft.failureConditions.length) def.failureConditions = draft.failureConditions.map(cleanCondition);
  return def;
}

/** Remove a phase and every reference to it in transitions. */
export function removePhase(draft: ProgressionDraft, index: number): ProgressionDraft {
  const name = draft.phases[index];
  const phases = draft.phases.filter((_, i) => i !== index);
  const transitions: ProgressionDraft['transitions'] = {};
  for (const [from, to] of Object.entries(draft.transitions)) {
    if (from === name) continue;
    transitions[from] = to ? to.filter((p) => p !== name) : null;
  }
  return { ...draft, phases, transitions };
}

export type ProblemSection =
  | 'phases'
  | 'transitions'
  | 'facts'
  | 'requiredConcernRefs'
  | 'allowedActions'
  | 'rubric'
  | 'successConditions'
  | 'failureConditions'
  | 'general';

/** Place each server problem against the section of the builder it concerns. */
export function groupProblems(problems: readonly string[]): Record<ProblemSection, string[]> {
  const out: Record<ProblemSection, string[]> = {
    phases: [], transitions: [], facts: [], requiredConcernRefs: [], allowedActions: [],
    rubric: [], successConditions: [], failureConditions: [], general: [],
  };
  for (const p of problems) {
    const head = p.split(/[.:[\s]/, 1)[0] ?? '';
    if (head === 'phases' || p.startsWith('an objection waits for phase')) out.phases.push(p);
    else if (head === 'transitions') out.transitions.push(p);
    else if (head === 'facts' || p.startsWith('fact ids')) out.facts.push(p);
    else if (head === 'requiredConcernRefs') out.requiredConcernRefs.push(p);
    else if (head === 'allowedActions') out.allowedActions.push(p);
    else if (head === 'rubric' || head === 'modalityRubrics') out.rubric.push(p);
    else if (head === 'successConditions') out.successConditions.push(p);
    else if (head === 'failureConditions') out.failureConditions.push(p);
    else out.general.push(p);
  }
  return out;
}

/**
 * Whether a rubric can grade a modality: it supports it and judges the
 * evidence a graded attempt there requires (the server decides; this mirrors
 * its listing so the picker can say why an option is unavailable).
 */
export function rubricGradesModality(r: RubricSummary, modality: RubricModality): { ok: boolean; why: string | null } {
  if (!r.modalities.includes(modality)) return { ok: false, why: 'does not support this modality' };
  if (r.gradableModalities && !r.gradableModalities.includes(modality)) {
    return { ok: false, why: modality === 'realtime_audio_video' ? 'cannot judge video evidence' : 'cannot judge the required evidence' };
  }
  return { ok: true, why: null };
}

/** Learner/author-facing wording for a not-gradable reason. */
export function reasonLabel(reason: AssessmentUnavailableReason | null): string {
  if (!reason) return '';
  switch (reason.code) {
    case 'NO_AUTHORED_RUBRIC': return 'no rubric authored';
    case 'MODALITY_NOT_AUTHORED': return 'no rubric named for this modality';
    case 'RUBRIC_MODALITY_UNSUPPORTED': return 'rubric does not support this modality';
    case 'RUBRIC_EVIDENCE_UNSUPPORTED': return `rubric cannot judge ${(reason.missingEvidence ?? []).join(', ') || 'the required'} evidence`;
    case 'UNKNOWN_RUBRIC': return 'unknown rubric';
    default: return reason.code;
  }
}

/** Problems from a failed save: the server's 422 `error.details.problems`. */
export function problemsFromError(error: unknown): string[] | null {
  const e = error as { status?: number; data?: { error?: { details?: { problems?: unknown } } } } | undefined;
  const problems = e?.data?.error?.details?.problems;
  if (e?.status === 422 && Array.isArray(problems)) return problems.filter((p): p is string => typeof p === 'string');
  return null;
}
